import React from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';

interface CommonFooterProps {
  onNavigateToLegal?: (page: 'privacy-policy' | 'terms-of-service' | 'refund-policy' | 'shipping-policy' | 'contact' | 'services' | 'advantages' | 'how-to-use' | 'faq') => void;
}

export const CommonFooter: React.FC<CommonFooterProps> = ({ onNavigateToLegal }) => {
  return (
    <footer className="bg-slate-900/50 border-t border-white/5 py-12 px-4 mt-auto">
      <div className="max-w-7xl mx-auto">
        <div className="text-center space-y-6">
          {/* Navigation Links */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4 text-sm">
            {onNavigateToLegal && (
              <>
                <button
                  onClick={() => onNavigateToLegal('services')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  Services
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('advantages')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  Advantages
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('how-to-use')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  How to Use
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('faq')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  FAQ
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('contact')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  Contact
                </button>
              </>
            )}
          </div>

          {/* Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4 text-xs">
            {onNavigateToLegal && (
              <>
                <button
                  onClick={() => onNavigateToLegal('privacy-policy')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Privacy Policy
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('terms-of-service')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Terms of Service
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('refund-policy')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Refund Policy
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('shipping-policy')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Shipping Policy
                </button>
              </>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <MapPin size={16} />
              <span>1209 MOUNTAIN ROAD PL NE STE R, ALBUQUERQUE, NM, 87110, USA</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Phone size={16} />
              <a href="tel:+19177958308" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                +1 (917) 795-8308
              </a>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Mail size={16} />
              <a href="mailto:contact@orcaseotools.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                contact@orcaseotools.com
              </a>
            </div>
          </div>

          {/* Payment Partner */}
          <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-slate-700/50">
            <a 
              href="https://stripe.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
              title="Powered by Stripe - Secure Payments"
            >
              <img 
                src="https://cdn.brandfetch.io/idxAg10C0L/theme/dark/logo.svg?c=1dxbfHSJFAPEGdCLU4o5B" 
                alt="Powered by Stripe" 
                className="h-8"
                onError={(e) => {
                  // Fallback to text if image fails
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = '<svg viewBox="0 0 60 25" class="h-6" xmlns="http://www.w3.org/2000/svg"><path d="M30.2 4.4c-2 0-3.3 1-3.3 2.6 0 2.8 4 2.3 4 3.5 0 .5-.4.7-1.1.7-.9 0-2.2-.4-3.2-1v3c1.1.5 2.2.7 3.2.7 2.1 0 3.5-1 3.5-2.6 0-2.9-4-2.4-4-3.5 0-.4.4-.6 1-.6.8 0 2 .3 2.8.8V5c-.9-.4-1.9-.6-2.9-.6zm-12.3 0c-1.2 0-1.9.6-2.4 1.4l-.1-1.2h-2.8v12.2l3.2-.7V14c.4.3 1 .8 2 .8 2 0 3.9-1.6 3.9-5.3 0-3.3-1.9-5.1-3.8-5.1zm-.6 7.8c-.7 0-1.1-.2-1.3-.6V8.4c.3-.3.7-.6 1.3-.6 1 0 1.7 1.1 1.7 2.2 0 1.2-.7 2.2-1.7 2.2zm-8-7.8c-1.8 0-3 .8-3 2.2 0 1.6 1.5 2 2.7 2.3 1 .2 1.3.4 1.3.7 0 .3-.3.5-.9.5-.8 0-1.7-.3-2.5-.7v2.5c.8.4 1.7.5 2.5.5 1.9 0 3.2-.8 3.2-2.3 0-1.7-1.5-2.1-2.7-2.4-.9-.2-1.2-.3-1.2-.6 0-.3.3-.4.8-.4.6 0 1.5.2 2.2.6V4.8c-.8-.3-1.5-.4-2.4-.4zm17.7 0l-3.2.7v9.5h3.2V4.4zm0-3.4l-3.2.7v2.5l3.2-.7V1zm4.3 3.5h-2.1v2.7h2.1v4.5c0 1.9 1 3 3 3 .9 0 1.6-.2 2-.4V12c-.3.1-.7.2-1.1.2-.6 0-.9-.2-.9-.9V7.2h2V4.6h-2v-2l-3 .6v1.3zm8 4.5c0-1 .6-1.6 1.3-1.6.7 0 1.2.6 1.2 1.5h-2.5zm5.6.8c0-3.2-1.5-5.4-4.3-5.4-2.8 0-4.5 2.2-4.5 5.3 0 3.5 1.9 5.2 4.7 5.2 1.4 0 2.4-.3 3.2-.8v-2.4c-.8.4-1.7.6-2.9.6-1.1 0-2.1-.4-2.2-1.7h5.9c0-.2.1-.5.1-.8z" fill="#635BFF"/></svg>';
                }}
              />
            </a>
          </div>

          {/* Copyright */}
          <div className="text-slate-500 text-xs mt-6">
            © {new Date().getFullYear()} Sofia Host LLC - Orcaseotools. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

